import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import {
  getAllOrders,
  updateOrderStatus,
  deleteOrder,
  downloadOrdersExcel,
  downloadOrdersPdf,
  shipOrder,
  editDelhiveryShipment,
  cancelDelhiveryShipment,
  updateDelhiveryEwaybill,
  generateDelhiveryLabel,
  requestDelhiveryPickup,
  manageReturn,
  refreshOrderTracking,
  downloadOrderInvoice
} from '../../services/orderService';
import formatPrice from '../../utils/formatPrice';
import { mediaUrl } from '../../utils/mediaUrl';
import "../../css/adminOrders.css";
import "../../css/phase1.css";
import { Table } from '../../components/Table';
import { useToast } from '../../contexts/ToastContext';
import {
  Search,
  X,
  FileSpreadsheet,
  FileText,
  Eye,
  Trash2,
  Copy,
  Check,
  Truck,
  Package,
  Clock,
  User,
  MapPin,
  CreditCard,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Phone,
  Mail,
  Calendar,
  Loader2,
  Pencil,
  Ban
} from 'lucide-react';

const STATUS_OPTIONS = ['Placed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered', 'Cancelled', 'Rejected', 'Returned'];

// Presentation only: maps an order status to its badge colour class (aop-pill-*)
const STATUS_TONE = {
  Placed: 'placed',
  Processing: 'processing',
  Shipped: 'shipped',
  'Out for Delivery': 'out-for-delivery',
  Delivered: 'delivered',
  Cancelled: 'cancelled',
  Rejected: 'rejected',
  Returned: 'returned'
};

const RETURN_LABEL = {
  requested: 'Return requested',
  approved: 'Return approved',
  picked_up: 'Return picked up',
  received: 'Return received',
  refunded: 'Refunded',
  rejected: 'Return rejected'
};

const PROGRESS_STEPS = ['Placed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered'];

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateShort = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const StatusPill = ({ tone, children }) => (
  <span className={`aop-pill aop-pill-${tone}`}>{children}</span>
);

const AdminOrdersPage = () => {
  const { addToast, confirm, prompt } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [activeOrder, setActiveOrder] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  const load = async (search = '', date = '', month = '') => {
    setLoading(true);
    setError('');
    try {
      const params = { search };
      if (date) params.date = date;
      if (month) params.month = month;
      const list = await getAllOrders(params);
      setOrders(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load orders:', err);
      const isNetworkError = err?.code === 'ERR_NETWORK' || !err?.response;
      const msg = isNetworkError
        ? 'Cannot connect to server. Please check if the backend is running on port 5001.'
        : err?.response?.data?.message || 'Failed to load orders.';
      setError(msg);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = searchParams.get('search') || '';
    const st = searchParams.get('status') || 'all';
    setSearchTerm(q);
    setStatusFilter(st);
    load(q);
  }, [searchParams]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    load(searchTerm, filterType === 'date' ? selectedDate : '', filterType === 'month' ? selectedMonth : '');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    load('', filterType === 'date' ? selectedDate : '', filterType === 'month' ? selectedMonth : '');
  };

  const applyUpdated = (updated) => {
    if (!updated?._id) return;
    setOrders((prev) => prev.map((o) => (o._id === updated._id ? { ...o, ...updated, user: o.user } : o)));
    setActiveOrder((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated, user: prev.user } : prev));
  };

  const updateStatus = async (id, status) => {
    const order = orders.find((o) => o._id === id);
    if (['Cancelled', 'Rejected'].includes(status)) {
      const paidOnline = order?.isPaid && order?.paymentMethod !== 'COD';
      const reason = await prompt({
        title: `${status === 'Cancelled' ? 'Cancel' : 'Reject'} order ${order?.orderId || ''}`,
        message:
          (paidOnline ? `The customer will be refunded ${formatPrice(order.totalPrice)} automatically.\n\n` : '') +
          'Reason (shown to the customer):',
        placeholder: 'Enter a reason',
        confirmLabel: status === 'Cancelled' ? 'Cancel order' : 'Reject order',
        danger: true
      });
      if (reason === null) return;
      try {
        applyUpdated(await updateOrderStatus(id, { status, note: reason }));
      } catch (err) {
        addToast(err.response?.data?.message || 'Could not update the order', 'error');
      }
      return;
    }
    try {
      applyUpdated(await updateOrderStatus(id, { status }));
    } catch (err) {
      addToast(err.response?.data?.message || 'Could not update the order', 'error');
    }
  };

  const updatePaid = async (id, isPaid) => {
    try {
      applyUpdated(await updateOrderStatus(id, { isPaid: isPaid === 'true' }));
    } catch (err) {
      addToast(err.response?.data?.message || 'Could not update payment status', 'error');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this order?',
      message: 'This permanently removes the order from the admin list.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await deleteOrder(id);
      if (activeOrder && activeOrder._id === id) setActiveOrder(null);
      load(searchTerm, filterType === 'date' ? selectedDate : '', filterType === 'month' ? selectedMonth : '');
    } catch (err) {
      addToast(err.response?.data?.message || 'Could not delete the order', 'error');
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    if (type === 'all') {
      setSelectedDate('');
      setSelectedMonth('');
      load(searchTerm);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) load(searchTerm, date, '');
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    if (month) load(searchTerm, '', month);
  };

  const handleResetFilters = () => {
    setFilterType('all');
    setSelectedDate('');
    setSelectedMonth('');
    setSearchTerm('');
    setStatusFilter('all');
    setSearchParams({});
    load('', '', '');
  };

  const currentExportParams = () => {
    const params = { search: searchTerm };
    if (filterType === 'date' && selectedDate) params.date = selectedDate;
    if (filterType === 'month' && selectedMonth) params.month = selectedMonth;
    return params;
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await downloadOrdersExcel(currentExportParams());
    } catch (error) {
      console.error('Error downloading Excel:', error);
      addToast('Failed to download Excel report', 'error');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadOrdersPdf(currentExportParams());
    } catch (error) {
      console.error('Failed to download orders PDF:', error);
      addToast('Failed to download PDF report', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Status Counts for Tab Badges
  const statusCounts = useMemo(() => {
    const counts = {
      all: orders.length,
      pending: 0,
      Placed: 0,
      Processing: 0,
      Shipped: 0,
      'Out for Delivery': 0,
      Delivered: 0,
      Cancelled: 0,
      Rejected: 0,
      Returned: 0
    };
    orders.forEach((o) => {
      if (counts[o.status] !== undefined) {
        counts[o.status]++;
      }
      if (['Placed', 'Processing'].includes(o.status)) {
        counts.pending++;
      }
    });
    return counts;
  }, [orders]);

  const displayedOrders = useMemo(() => {
    if (statusFilter === 'all') return orders;
    if (statusFilter === 'pending') {
      return orders.filter((o) => ['Placed', 'Processing'].includes(o.status));
    }
    return orders.filter((o) => o.status === statusFilter);
  }, [orders, statusFilter]);

  const STATUS_TABS = [
    { id: 'all', label: 'All Orders' },
    { id: 'pending', label: 'Pending' },
    { id: 'Placed', label: 'Placed' },
    { id: 'Processing', label: 'Processing' },
    { id: 'Shipped', label: 'Shipped' },
    { id: 'Out for Delivery', label: 'Out for Delivery' },
    { id: 'Delivered', label: 'Delivered' },
    { id: 'Cancelled', label: 'Cancelled' },
    { id: 'Rejected', label: 'Rejected' },
    { id: 'Returned', label: 'Returned' }
  ];

  // Table column definitions
  const columns = useMemo(
    () => [
      {
        Header: 'Order',
        id: 'orderId',
        accessor: (o) => o.orderId,
        Cell: ({ row }) => {
          const o = row.original;
          return (
            <button className="aop-order-cell" onClick={() => setActiveOrder(o)} title="Click to view details">
              <span className="aop-order-id">#{o.orderId}</span>
              <span className="aop-order-date">
                <Calendar size={11} /> {formatDateShort(o.createdAt)}
              </span>
            </button>
          );
        }
      },
      {
        Header: 'Customer',
        id: 'user',
        accessor: (o) => o.user?.name || o.shippingAddress?.fullName || 'Guest',
        Cell: ({ row }) => {
          const o = row.original;
          const name = o.user?.name || o.shippingAddress?.fullName || 'Guest';
          const sub = o.user?.email || o.shippingAddress?.phone || '—';
          const initial = name ? name.charAt(0).toUpperCase() : 'G';
          return (
            <div className="aop-customer-cell">
              <div className="aop-customer-avatar">{initial}</div>
              <div className="aop-customer-info">
                <span className="aop-customer-name" title={name}>{name}</span>
                <span className="aop-customer-sub" title={sub}>{sub}</span>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'Items',
        id: 'items',
        disableSortBy: true,
        accessor: (o) => o.orderItems?.length || 0,
        Cell: ({ row }) => {
          const items = row.original.orderItems || [];
          const first = items[0];
          const totalQty = items.reduce((sum, it) => sum + (it.qty || 0), 0);
          if (!first) return <span className="aop-muted">No items</span>;
          return (
            <div className="aop-items-cell">
              <img className="aop-item-thumb" src={mediaUrl(first.image)} alt={first.name} />
              <div className="aop-items-info">
                <span className="aop-item-name" title={first.name}>{first.name}</span>
                <span className="aop-item-meta">
                  {first.size ? `${first.size} · ` : ''}{first.color ? `${first.color} · ` : ''}Qty {totalQty}
                  {items.length > 1 && (
                    <span className="aop-more-items-tag">+{items.length - 1} more</span>
                  )}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'Payment',
        id: 'payment',
        disableSortBy: true,
        Cell: ({ row }) => {
          const o = row.original;
          return (
            <div className="aop-payment-cell">
              <span className="aop-method-tag">{o.paymentMethod || '—'}</span>
              <select
                className={`aop-select-pill ${o.isPaid ? 'aop-pill-success' : 'aop-pill-warn'}`}
                value={o.isPaid.toString()}
                onChange={(e) => updatePaid(o._id, e.target.value)}
              >
                <option value="true">Paid</option>
                <option value="false">Unpaid</option>
              </select>
            </div>
          );
        }
      },
      {
        Header: 'Total',
        id: 'total',
        accessor: (o) => o.totalPrice,
        Cell: ({ row }) => {
          const o = row.original;
          return (
            <div className="aop-total-cell">
              <span className="aop-total-amount">{formatPrice(o.totalPrice)}</span>
              <span className="aop-total-sub">
                {formatPrice(o.itemsPrice)} + {formatPrice(o.taxPrice)} tax
              </span>
            </div>
          );
        }
      },
      {
        Header: 'Status',
        id: 'status',
        disableSortBy: true,
        Cell: ({ row }) => {
          const o = row.original;
          const tone = STATUS_TONE[o.status] || 'neutral';
          const rr = o.returnRequest?.status;
          return (
            <div className="aop-status-cell">
              <select
                className={`aop-select-pill aop-pill-${tone}`}
                value={o.status || 'Placed'}
                onChange={(e) => updateStatus(o._id, e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {rr && rr !== 'none' && (
                <span className="aop-pill aop-pill-warn aop-pill-sm">
                  <RotateCcw size={10} /> {RETURN_LABEL[rr] || rr}
                </span>
              )}
            </div>
          );
        }
      },
      {
        Header: 'Delivery',
        id: 'delivery',
        disableSortBy: true,
        Cell: ({ row }) => {
          const o = row.original;
          if (['Cancelled', 'Rejected'].includes(o.status)) {
            return (
              <StatusPill tone="danger">
                <AlertCircle size={12} /> {o.status}
              </StatusPill>
            );
          }
          if (o.status === 'Returned') {
            return (
              <StatusPill tone="returned">
                <RotateCcw size={12} /> Returned
              </StatusPill>
            );
          }
          return o.isDelivered ? (
            <div className="aop-total-cell">
              <StatusPill tone="success">
                <CheckCircle2 size={12} /> Delivered
              </StatusPill>
              <span className="aop-total-sub">{formatDateShort(o.deliveredAt)}</span>
            </div>
          ) : (
            <StatusPill tone="neutral">
              <Clock size={12} /> Pending
            </StatusPill>
          );
        }
      },
      {
        Header: 'Action',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => {
          const o = row.original;
          return (
            <div className="aop-actions-cell">
              <button
                className="aop-btn-view"
                onClick={() => setActiveOrder(o)}
                title="View full order details"
              >
                <Eye size={13} /> View
              </button>
              <button
                className="aop-btn-delete"
                onClick={() => handleDelete(o._id)}
                aria-label="Delete order"
                title="Delete order"
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        }
      }
    ],
    [orders]
  );

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Orders Management" />

        <div className="aop-container">
          {/* Page heading with exports */}
          <div className="aop-heading">
            <div className="aop-title-group">
              <h1 className="aop-title">Orders</h1>
              <p className="aop-subtitle">
                Manage customer orders, Shiprocket logistics, invoices, returns, and refunds.
              </p>
            </div>

            <div className="aop-header-actions">
              <span className="aop-title-badge">
                {displayedOrders.length} {displayedOrders.length === 1 ? 'Order' : 'Orders'}
              </span>
              <button
                type="button"
                className="aop-btn-export aop-btn-export-excel"
                onClick={handleDownloadExcel}
                disabled={downloadingExcel}
                title="Export orders to Excel format"
              >
                <FileSpreadsheet size={15} />
                {downloadingExcel ? 'Exporting…' : 'Export Excel'}
              </button>

              <button
                type="button"
                className="aop-btn-export aop-btn-export-pdf"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                title="Export orders to PDF format"
              >
                <FileText size={15} />
                {downloadingPdf ? 'Preparing…' : 'Download PDF'}
              </button>
            </div>
          </div>

          <div className="aop-controls-card">
          {/* Status Filter Tabs */}
          <div className="aop-tabs-wrapper" role="tablist">
            {STATUS_TABS.map((tab) => {
              const count = statusCounts[tab.id] || 0;
              const isActive = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`aop-tab-pill ${isActive ? 'active' : ''}`}
                  onClick={() => setStatusFilter(tab.id)}
                >
                  <span>{tab.label}</span>
                  <span className="aop-tab-count">{count}</span>
                </button>
              );
            })}
          </div>

          {/* Search & Filter Toolbar */}
          <div className="aop-toolbar">
            <form onSubmit={handleSearch} className="aop-search-box">
              <Search size={16} className="aop-search-icon" />
              <input
                type="text"
                placeholder="Search by Order ID, customer, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="aop-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="aop-search-clear"
                  onClick={handleClearSearch}
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </form>

            <div className="aop-filter-group">
              <select
                className="aop-filter-select"
                value={filterType}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                <option value="all">All Dates</option>
                <option value="date">Filter by Day</option>
                <option value="month">Filter by Month</option>
              </select>

              {filterType === 'date' && (
                <input
                  type="date"
                  className="aop-filter-date-input"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                />
              )}

              {filterType === 'month' && (
                <input
                  type="month"
                  className="aop-filter-date-input"
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                />
              )}

              {(filterType !== 'all' || searchTerm || statusFilter !== 'all') && (
                <button
                  type="button"
                  className="aop-btn-reset-filter"
                  onClick={handleResetFilters}
                  title="Reset all filters"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          </div>

          {/* Orders Table, Loading, or Error State */}
          {loading ? (
            <div className="aop-loading-wrapper">
              <div className="aop-progress-bar-container">
                <div className="aop-progress-bar-indeterminate" />
              </div>
              <div className="aop-loading-content">
                <Loader2 size={24} className="aop-spin aop-loading-icon" />
                <span>Loading orders...</span>
              </div>
            </div>
          ) : error ? (
            <div className="aop-error-banner">
              <div className="aop-error-info">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
              <button
                type="button"
                className="aop-btn-retry"
                onClick={() => load(searchTerm, filterType === 'date' ? selectedDate : '', filterType === 'month' ? selectedMonth : '')}
              >
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          ) : (
            <Table columns={columns} data={displayedOrders} show />
          )}
        </div>
      </div>

      {/* Slide-out Order Details Drawer */}
      {activeOrder && (
        <OrderDrawer
          order={activeOrder}
          onClose={() => setActiveOrder(null)}
          onStatusChange={updateStatus}
          onPaidChange={updatePaid}
          onDelete={handleDelete}
          onUpdated={applyUpdated}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Order Details Slide-Out Drawer
// ------------------------------------------------------------
const OrderDrawer = ({ order, onClose, onStatusChange, onPaidChange, onDelete, onUpdated }) => {
  const addr = order.shippingAddress || {};
  const tone = STATUS_TONE[order.status] || 'neutral';
  const isCancelledOrRejected = ['Cancelled', 'Rejected'].includes(order.status);
  const [copied, setCopied] = useState(false);

  const handleCopyId = () => {
    navigator.clipboard?.writeText(order.orderId || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentStepIdx = PROGRESS_STEPS.indexOf(order.status);

  return (
    <div className="aop-drawer-backdrop" onClick={onClose}>
      <div className="aop-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="aop-drawer-header">
          <div className="aop-drawer-header-left">
            <p className="aop-drawer-eyebrow">Order Details</p>
            <div className="aop-drawer-title-row">
              <h2 className="aop-drawer-id">#{order.orderId}</h2>
              <button
                className="aop-btn-copy-id"
                onClick={handleCopyId}
                title="Copy Order ID"
              >
                {copied ? <Check size={15} className="aop-copy-ok" /> : <Copy size={15} />}
              </button>
            </div>
          </div>
          <button className="aop-drawer-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="aop-drawer-body">
          {/* Quick Actions / Status Pills */}
          <div className="aop-drawer-quick-actions">
            <select
              className={`aop-select-pill aop-pill-${tone}`}
              value={order.status || 'Placed'}
              onChange={(e) => onStatusChange(order._id, e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              className={`aop-select-pill ${order.isPaid ? 'aop-pill-success' : 'aop-pill-warn'}`}
              value={order.isPaid.toString()}
              onChange={(e) => onPaidChange(order._id, e.target.value)}
            >
              <option value="true">Paid</option>
              <option value="false">Unpaid</option>
            </select>

            {order.isDelivered ? (
              <StatusPill tone="success">
                <CheckCircle2 size={12} /> Delivered
              </StatusPill>
            ) : (
              <StatusPill tone="neutral">
                <Clock size={12} /> In Transit / Pending
              </StatusPill>
            )}
          </div>

          {/* Cancellation / Rejection Note Alert */}
          {isCancelledOrRejected && order.cancelReason && (
            <div className="aop-reason-alert aop-reason-alert-coral">
              <AlertCircle size={18} className="aop-alert-icon" />
              <div>
                <strong>{order.status} Reason:</strong> {order.cancelReason}
              </div>
            </div>
          )}

          {/* Progress Stepper (if not cancelled/rejected) */}
          {!isCancelledOrRejected && (
            <div className="aop-stepper-card">
              <p className="aop-stepper-title">Order Status Progress</p>
              <div className="aop-stepper">
                <div className="aop-stepper-line" />
                {PROGRESS_STEPS.map((step, idx) => {
                  const isCompleted = currentStepIdx >= idx;
                  const isActive = order.status === step;
                  return (
                    <div
                      key={step}
                      className={`aop-stepper-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                    >
                      <div className="aop-stepper-dot">
                        {isCompleted && !isActive ? (
                          <Check size={13} strokeWidth={3} />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <span className="aop-stepper-label">{step}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Customer & Delivery Card */}
          <div className="aop-drawer-card">
            <div className="aop-drawer-card-header">
              <h3 className="aop-drawer-card-title">
                <User size={16} /> Customer & Delivery Address
              </h3>
            </div>
            <dl className="aop-info-grid">
              <dt>Name</dt>
              <dd>{order.user?.name || addr.fullName || 'Guest checkout'}</dd>
              <dt>Phone</dt>
              <dd>
                {addr.phone ? (
                  <a href={`tel:${addr.phone}`} className="aop-link">
                    {addr.phone}
                  </a>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Email</dt>
              <dd>
                {order.user?.email ? (
                  <a href={`mailto:${order.user.email}`} className="aop-link">
                    {order.user.email}
                  </a>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Address</dt>
              <dd>
                {addr.addressLine1}
                {addr.addressLine2 ? `, ${addr.addressLine2}` : ''}, {addr.city}, {addr.state} - {addr.pincode}
              </dd>
            </dl>
          </div>

          {/* Courier & Shipping Fulfilment Card */}
          <FulfilmentSection key={`f-${order._id}`} order={order} onUpdated={onUpdated} />

          {/* Returns & Refund Section (if applicable) */}
          <ReturnSection key={`r-${order._id}`} order={order} onUpdated={onUpdated} />

          {/* Ordered Items Card */}
          <div className="aop-drawer-card">
            <div className="aop-drawer-card-header">
              <h3 className="aop-drawer-card-title">
                <Package size={16} /> Ordered Items ({order.orderItems?.length || 0})
              </h3>
            </div>
            <div className="aop-drawer-items-list">
              {(order.orderItems || []).map((it, i) => (
                <div className="aop-drawer-item-row" key={i}>
                  <img src={mediaUrl(it.image)} alt={it.name} className="aop-drawer-item-img" />
                  <div className="aop-drawer-item-details">
                    <span className="aop-drawer-item-title">{it.name}</span>
                    <div className="aop-drawer-item-chips">
                      {it.size && <span className="aop-drawer-item-chip">Size: {it.size}</span>}
                      {it.color && <span className="aop-drawer-item-chip">Color: {it.color}</span>}
                      <span>Qty: {it.qty} × {formatPrice(it.price)}</span>
                    </div>
                  </div>
                  <span className="aop-drawer-item-price">{formatPrice(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment & Summary Card */}
          <div className="aop-drawer-card">
            <div className="aop-drawer-card-header">
              <h3 className="aop-drawer-card-title">
                <CreditCard size={16} /> Payment & Summary
              </h3>
              <span className={`aop-pill ${order.isPaid ? 'aop-pill-success' : 'aop-pill-warn'}`}>
                {order.isPaid ? 'Paid' : 'Unpaid'}
              </span>
            </div>

            <div className="aop-breakdown-row">
              <span>Items Subtotal</span>
              <span>{formatPrice(order.itemsPrice)}</span>
            </div>
            <div className="aop-breakdown-row">
              <span>Shipping Fee</span>
              <span>{order.shippingPrice ? formatPrice(order.shippingPrice) : 'Free'}</span>
            </div>
            {order.discountPrice > 0 && (
              <div className="aop-breakdown-row aop-breakdown-row-discount">
                <span>Coupon {order.coupon?.code ? `(${order.coupon.code})` : ''}</span>
                <span>- {formatPrice(order.discountPrice)}</span>
              </div>
            )}
            {order.codFee > 0 && (
              <div className="aop-breakdown-row">
                <span>COD Handling Fee</span>
                <span>{formatPrice(order.codFee)}</span>
              </div>
            )}
            <div className="aop-breakdown-row">
              <span>GST / Tax {order.taxIncluded ? '(included)' : ''}</span>
              <span>{formatPrice(order.taxPrice)}</span>
            </div>
            <div className="aop-breakdown-row aop-breakdown-row-total">
              <span>Grand Total</span>
              <span>{formatPrice(order.totalPrice)}</span>
            </div>

            <dl className="aop-info-grid aop-info-grid-divided">
              <dt>Method</dt>
              <dd>{order.paymentMethod === 'COD' ? 'Cash on Delivery (COD)' : `Online (${order.paymentMethod || 'Razorpay'})`}</dd>
              {order.paymentResult?.id && (
                <>
                  <dt>Payment ID</dt>
                  <dd className="aop-mono">{order.paymentResult.id}</dd>
                </>
              )}
              {order.refund?.status && order.refund.status !== 'none' && (
                <>
                  <dt>Refund</dt>
                  <dd className="aop-text-danger">
                    {order.refund.status.toUpperCase()} ({formatPrice(order.refund.amount)})
                    {order.refund.razorpayRefundId ? ` · ID: ${order.refund.razorpayRefundId}` : ''}
                    {order.refund.reference ? ` · Ref: ${order.refund.reference}` : ''}
                  </dd>
                </>
              )}
            </dl>
          </div>

          {/* Audit Trail Timeline */}
          <div className="aop-drawer-card">
            <div className="aop-drawer-card-header">
              <h3 className="aop-drawer-card-title">
                <Clock size={16} /> Status & Audit Trail
              </h3>
            </div>
            <div className="aop-timeline">
              <div className="aop-timeline-item">
                <div className="aop-timeline-dot" />
                <div className="aop-timeline-content">
                  <span className="aop-timeline-title">Order Placed</span>
                  <span className="aop-timeline-time">{formatDate(order.createdAt)}</span>
                </div>
              </div>
              {order.paidAt && (
                <div className="aop-timeline-item">
                  <div className="aop-timeline-dot aop-timeline-dot-success" />
                  <div className="aop-timeline-content">
                    <span className="aop-timeline-title">Payment Confirmed</span>
                    <span className="aop-timeline-time">{formatDate(order.paidAt)}</span>
                  </div>
                </div>
              )}
              {order.statusHistory?.length > 0 &&
                [...order.statusHistory].reverse().map((h, i) => (
                  <div className="aop-timeline-item" key={i}>
                    <div className="aop-timeline-dot" />
                    <div className="aop-timeline-content">
                      <span className="aop-timeline-title">
                        {h.status} {h.note ? `— ${h.note}` : ''} {h.by ? `(${h.by})` : ''}
                      </span>
                      <span className="aop-timeline-time">{formatDate(h.at)}</span>
                    </div>
                  </div>
                ))}
              {order.deliveredAt && (
                <div className="aop-timeline-item">
                  <div className="aop-timeline-dot aop-timeline-dot-success" />
                  <div className="aop-timeline-content">
                    <span className="aop-timeline-title">Delivered to Customer</span>
                    <span className="aop-timeline-time">{formatDate(order.deliveredAt)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Danger Zone: Delete Order */}
          <div className="aop-drawer-danger-zone">
            <button
              type="button"
              className="aop-btn-delete-order"
              onClick={() => onDelete(order._id)}
            >
              <Trash2 size={16} /> Delete Order from Records
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// Fulfilment & Shipping Section
// ------------------------------------------------------------
const FulfilmentSection = ({ order, onUpdated }) => {
  const { confirm } = useToast();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [manual, setManual] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingEwb, setEditingEwb] = useState(false);
  const [ewbForm, setEwbForm] = useState({
    dcn: order.invoice?.number || '',
    ewbn: order.shipment?.ewaybillNumber || ''
  });
  const [form, setForm] = useState({ courierName: '', awbCode: '', trackingUrl: '' });
  const shipment = order.shipment || {};
  const addr = order.shippingAddress || {};
  const [editForm, setEditForm] = useState({
    name: addr.fullName || '',
    phone: addr.phone || '',
    add: [addr.addressLine1, addr.addressLine2].filter(Boolean).join(', '),
    products_desc: '',
    gm: '',
    shipment_length: '',
    shipment_width: '',
    shipment_height: '',
    pt: '',
    cod: order.totalPrice || ''
  });
  const closed = ['Cancelled', 'Rejected', 'Returned'].includes(order.status);
  const canShip = !closed && order.status !== 'Delivered' && !shipment.awbCode;
  const partlyBooked = shipment.shiprocketOrderId && (!shipment.awbCode || !shipment.labelUrl);
  const courierStatus = String(shipment.lastStatus || '').toLowerCase();
  const blockedEdit = ['dispatched', 'delivered', 'dto', 'rto', 'lost', 'closed', 'cancel'].some((key) =>
    courierStatus.includes(key)
  );
  const canEditDelhivery =
    shipment.provider === 'delhivery' &&
    shipment.awbCode &&
    !closed &&
    order.status !== 'Delivered' &&
    !blockedEdit;
  const needsEwaybill = Number(order.totalPrice) > 50000;
  const canUpdateEwaybill =
    shipment.provider === 'delhivery' &&
    shipment.awbCode &&
    !['Cancelled', 'Rejected'].includes(order.status);

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      const updated = await fn();
      if (updated) onUpdated(updated);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="aop-drawer-card">
      <div className="aop-drawer-card-header">
        <h3 className="aop-drawer-card-title">
          <Truck size={16} /> Shipping & Courier Logistics
        </h3>
        {shipment.awbCode ? (
          <span className="aop-pill aop-pill-info">
            AWB: {shipment.awbCode}
          </span>
        ) : (
          <span className="aop-pill aop-pill-neutral">
            {closed ? 'Closed' : 'Not Shipped'}
          </span>
        )}
      </div>

      {shipment.awbCode ? (
        <dl className="aop-info-grid">
          <dt>Courier</dt>
          <dd>{shipment.courierName || (shipment.provider === 'delhivery' ? 'Delhivery' : 'Courier')}</dd>
          <dt>AWB Code</dt>
          <dd className="aop-mono">{shipment.awbCode}</dd>
          <dt>Courier Status</dt>
          <dd>{shipment.lastStatus || 'In Transit'}</dd>
          {shipment.etd && (
            <>
              <dt>Expected Date</dt>
              <dd>{shipment.etd}</dd>
            </>
          )}
          {shipment.pickupScheduledAt && (
            <>
              <dt>Pickup Scheduled</dt>
              <dd>{formatDateShort(shipment.pickupScheduledAt)}</dd>
            </>
          )}
          {shipment.pickupRequestId && (
            <>
              <dt>Pickup Request</dt>
              <dd className="aop-mono">{shipment.pickupRequestId}</dd>
            </>
          )}
          {order.invoice?.number && (
            <>
              <dt>Invoice No.</dt>
              <dd>{order.invoice.number}</dd>
            </>
          )}
          {shipment.ewaybillNumber && (
            <>
              <dt>E-waybill</dt>
              <dd className="aop-mono">{shipment.ewaybillNumber}</dd>
            </>
          )}
          {needsEwaybill && !shipment.ewaybillNumber && (
            <>
              <dt>E-waybill</dt>
              <dd>Required (order above ₹50,000)</dd>
            </>
          )}
        </dl>
      ) : (
        <p className="aop-drawer-line">
          {closed
            ? 'Order is closed (no shipping required).'
            : 'Shipment has not been dispatched yet. Register the warehouse, then Ship with Delhivery.'}
        </p>
      )}

      {shipment.activities?.length > 0 && (
        <ol className="aop-scan-list">
          {shipment.activities.slice(0, 12).map((scan, index) => (
            <li key={`${scan.date}-${index}`}>
              <strong>{scan.status || scan.activity}</strong>
              {scan.location ? ` · ${scan.location}` : ''}
              {scan.date ? <span>{scan.date}</span> : null}
            </li>
          ))}
        </ol>
      )}

      {error && (
        <div className="aop-reason-alert aop-reason-alert-coral aop-reason-alert-spaced">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="aop-card-actions">
        {(canShip || partlyBooked) && !manual && (
          <button
            type="button"
            className="aop-btn-primary"
            disabled={!!busy}
            onClick={() => run('ship', () => shipOrder(order._id))}
          >
            <Truck size={14} />
            {busy === 'ship'
              ? 'Booking Courier…'
              : partlyBooked
                ? 'Retry Shiprocket'
                : 'Ship with Delhivery'}
          </button>
        )}

        {canShip && !partlyBooked && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => setManual((m) => !m)}
          >
            {manual ? 'Cancel Manual' : 'Ship Manually'}
          </button>
        )}
        {canShip && (
          <Link to="/admin/courier-warehouse" className="aop-btn-secondary">
            Register warehouse
          </Link>
        )}

        {shipment.labelUrl && (
          <a
            className="aop-btn-secondary"
            href={shipment.labelUrl}
            target="_blank"
            rel="noreferrer"
          >
            <FileText size={14} /> Shipping Label
          </a>
        )}

        {shipment.provider === 'delhivery' && shipment.awbCode && !closed && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => run('pickup', () => requestDelhiveryPickup(order._id))}
          >
            <Truck size={14} />
            {busy === 'pickup'
              ? 'Requesting pickup…'
              : shipment.pickupRequestId
                ? 'Request pickup again'
                : 'Request pickup'}
          </button>
        )}

        {shipment.provider === 'delhivery' && shipment.awbCode && !closed && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => run('label', () => generateDelhiveryLabel(order._id, { pdf: true, pdfSize: 'A4' }))}
          >
            <FileText size={14} />
            {busy === 'label'
              ? 'Generating label…'
              : shipment.labelUrl
                ? 'Regenerate label'
                : 'Generate shipping label'}
          </button>
        )}

        {shipment.trackingUrl && (
          <a
            className="aop-btn-secondary"
            href={shipment.trackingUrl}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink size={14} /> Live Track
          </a>
        )}

        {['shiprocket', 'delhivery'].includes(shipment.provider) && shipment.awbCode && order.status !== 'Delivered' && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => run('track', () => refreshOrderTracking(order._id))}
          >
            <RefreshCw size={14} className={busy === 'track' ? 'spin aop-spin' : ''} />
            {busy === 'track' ? 'Refreshing…' : 'Refresh Tracking'}
          </button>
        )}

        {canEditDelhivery && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => {
              setManual(false);
              setEditing((open) => !open);
            }}
          >
            <Pencil size={14} />
            {editing ? 'Cancel edit' : 'Edit shipment'}
          </button>
        )}

        {canUpdateEwaybill && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => {
              setManual(false);
              setEditing(false);
              setEditingEwb((open) => !open);
              setEwbForm({
                dcn: order.invoice?.number || '',
                ewbn: shipment.ewaybillNumber || ''
              });
            }}
          >
            <FileText size={14} />
            {editingEwb ? 'Close e-waybill' : needsEwaybill ? 'Update e-waybill' : 'Add e-waybill'}
          </button>
        )}

        {canEditDelhivery && (
          <button
            type="button"
            className="aop-btn-danger"
            disabled={!!busy}
            onClick={async () => {
              const ok = await confirm({
                title: 'Cancel Delhivery shipment?',
                message: 'This cancels the courier booking for this waybill. The order itself is not cancelled.',
                confirmLabel: 'Cancel shipment',
                danger: true
              });
              if (!ok) return;
              setEditing(false);
              run('cancelShip', () => cancelDelhiveryShipment(order._id));
            }}
          >
            <Ban size={14} />
            {busy === 'cancelShip' ? 'Cancelling…' : 'Cancel shipment'}
          </button>
        )}

        {!['Cancelled', 'Rejected'].includes(order.status) && (
          <button
            type="button"
            className="aop-btn-secondary"
            disabled={!!busy}
            onClick={() => run('invoice', async () => { await downloadOrderInvoice(order); return null; })}
          >
            <FileText size={14} />
            {busy === 'invoice' ? 'Preparing…' : 'GST Tax Invoice'}
          </button>
        )}
      </div>

      {editingEwb && canUpdateEwaybill && (
        <form
          className="aop-inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            run('ewb', async () => {
              const updated = await updateDelhiveryEwaybill(order._id, {
                dcn: ewbForm.dcn,
                ewbn: ewbForm.ewbn
              });
              setEditingEwb(false);
              return updated;
            });
          }}
        >
          <p className="aop-drawer-line">
            Indian law requires an e-waybill when the shipment value is above ₹50,000.
            Invoice number is sent as dcn; the e-waybill number as ewbn.
          </p>
          <label>
            Invoice number (dcn)
            <input
              value={ewbForm.dcn}
              onChange={(e) => setEwbForm((f) => ({ ...f, dcn: e.target.value }))}
              placeholder="GST invoice number"
              required
            />
          </label>
          <label>
            E-waybill number (ewbn)
            <input
              value={ewbForm.ewbn}
              onChange={(e) => setEwbForm((f) => ({ ...f, ewbn: e.target.value }))}
              placeholder="12-digit e-waybill"
              required
            />
          </label>
          <div className="aop-form-actions">
            <button type="submit" className="aop-btn-primary" disabled={!!busy}>
              {busy === 'ewb' ? 'Updating…' : 'Update on Delhivery'}
            </button>
            <button type="button" className="aop-btn-secondary" onClick={() => setEditingEwb(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {editing && canEditDelhivery && (
        <form
          className="aop-inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            const payload = {};
            ['name', 'phone', 'add', 'products_desc', 'gm', 'shipment_length', 'shipment_width', 'shipment_height'].forEach(
              (key) => {
                if (String(editForm[key] || '').trim()) payload[key] = editForm[key];
              }
            );
            if (editForm.pt) {
              payload.pt = editForm.pt;
              if (editForm.pt === 'COD') payload.cod = editForm.cod;
            }
            run('edit', async () => {
              const updated = await editDelhiveryShipment(order._id, payload);
              setEditing(false);
              return updated;
            });
          }}
        >
          <p className="aop-drawer-line">
            Delhivery allows edits only in Manifested, In Transit, or Pending. Payment can change
            COD → Prepaid, or Prepaid → COD if you enter a COD amount.
          </p>
          <label>
            Consignee name
            <input
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label>
            Phone
            <input
              value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
              inputMode="numeric"
              maxLength={10}
            />
          </label>
          <label>
            Address
            <input
              value={editForm.add}
              onChange={(e) => setEditForm((f) => ({ ...f, add: e.target.value }))}
            />
          </label>
          <label>
            Product description
            <input
              value={editForm.products_desc}
              onChange={(e) => setEditForm((f) => ({ ...f, products_desc: e.target.value }))}
            />
          </label>
          <label>
            Weight (grams)
            <input
              value={editForm.gm}
              onChange={(e) => setEditForm((f) => ({ ...f, gm: e.target.value }))}
              inputMode="decimal"
            />
          </label>
          <label>
            Length (cm)
            <input
              value={editForm.shipment_length}
              onChange={(e) => setEditForm((f) => ({ ...f, shipment_length: e.target.value }))}
              inputMode="decimal"
            />
          </label>
          <label>
            Width (cm)
            <input
              value={editForm.shipment_width}
              onChange={(e) => setEditForm((f) => ({ ...f, shipment_width: e.target.value }))}
              inputMode="decimal"
            />
          </label>
          <label>
            Height (cm)
            <input
              value={editForm.shipment_height}
              onChange={(e) => setEditForm((f) => ({ ...f, shipment_height: e.target.value }))}
              inputMode="decimal"
            />
          </label>
          <label>
            Payment mode
            <select
              value={editForm.pt}
              onChange={(e) => setEditForm((f) => ({ ...f, pt: e.target.value }))}
            >
              <option value="">Keep current ({order.paymentMethod === 'COD' ? 'COD' : 'Prepaid'})</option>
              {order.paymentMethod === 'COD' ? (
                <option value="Pre-paid">Convert COD to Prepaid</option>
              ) : (
                <option value="COD">Convert Prepaid to COD</option>
              )}
            </select>
          </label>
          {editForm.pt === 'COD' && (
            <label>
              COD amount
              <input
                value={editForm.cod}
                onChange={(e) => setEditForm((f) => ({ ...f, cod: e.target.value }))}
                inputMode="decimal"
                required
              />
            </label>
          )}
          <div className="aop-form-actions">
            <button type="submit" className="aop-btn-primary" disabled={!!busy}>
              {busy === 'edit' ? 'Updating…' : 'Update on Delhivery'}
            </button>
            <button type="button" className="aop-btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {manual && (
        <div className="aop-inline-form">
          <label>
            Courier Partner Name:
            <input
              value={form.courierName}
              onChange={(e) => setForm((f) => ({ ...f, courierName: e.target.value }))}
              placeholder="e.g. DTDC, Professional, India Post"
            />
          </label>
          <label>
            AWB Tracking Number:
            <input
              value={form.awbCode}
              onChange={(e) => setForm((f) => ({ ...f, awbCode: e.target.value.trim() }))}
              placeholder="e.g. D123456789"
            />
          </label>
          <label>
            Tracking URL (optional):
            <input
              value={form.trackingUrl}
              onChange={(e) => setForm((f) => ({ ...f, trackingUrl: e.target.value.trim() }))}
              placeholder="https://track.courier.com/..."
            />
          </label>
          <div className="aop-form-actions">
            <button
              type="button"
              className="aop-btn-primary"
              disabled={!!busy || !form.courierName || !form.awbCode}
              onClick={() =>
                run('manual', async () => {
                  const updated = await shipOrder(order._id, { manual: true, ...form });
                  setManual(false);
                  return updated;
                })
              }
            >
              {busy === 'manual' ? 'Saving…' : 'Mark as Shipped'}
            </button>
            <button
              type="button"
              className="aop-btn-secondary"
              onClick={() => setManual(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Returns & Refund Resolution Section
// ------------------------------------------------------------
const ReturnSection = ({ order, onUpdated }) => {
  const rr = order.returnRequest || {};
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState(order.totalPrice);

  if (!rr.status || rr.status === 'none') return null;

  const act = async (action, extra = {}) => {
    setBusy(action);
    setError('');
    try {
      onUpdated(await manageReturn(order._id, { action, note, ...extra }));
      setNote('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update the return');
    } finally {
      setBusy('');
    }
  };

  const isCod = order.paymentMethod === 'COD';

  return (
    <div className="aop-drawer-card">
      <div className="aop-drawer-card-header">
        <h3 className="aop-drawer-card-title">
          <RotateCcw size={16} /> Return Request Resolution
        </h3>
        <span className="aop-pill aop-pill-warn">
          {RETURN_LABEL[rr.status] || rr.status}
        </span>
      </div>

      <dl className="aop-info-grid">
        <dt>Return Reason</dt>
        <dd>{rr.reason || '—'}</dd>
        {rr.comments && (
          <>
            <dt>Customer Note</dt>
            <dd>{rr.comments}</dd>
          </>
        )}
        <dt>Requested At</dt>
        <dd>{formatDate(rr.requestedAt)}</dd>
        {isCod && (
          <>
            <dt>Refund UPI</dt>
            <dd className="aop-mono">{rr.upiId || '—'}</dd>
          </>
        )}
        {rr.pickup?.shiprocketOrderId && (
          <>
            <dt>Pickup Booking</dt>
            <dd>Shiprocket #{rr.pickup.shiprocketOrderId}</dd>
          </>
        )}
        {rr.adminNote && (
          <>
            <dt>Admin Note</dt>
            <dd>{rr.adminNote}</dd>
          </>
        )}
      </dl>

      {error && (
        <div className="aop-reason-alert aop-reason-alert-coral aop-reason-alert-spaced">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {rr.status === 'requested' && (
        <div className="aop-inline-form">
          <label>
            Note to Customer (required to reject):
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Approved for reverse pickup / Rejected due to policy..."
            />
          </label>
          <div className="aop-form-actions">
            <button
              type="button"
              className="aop-btn-primary"
              disabled={!!busy}
              onClick={() => act('approve')}
            >
              {busy === 'approve' ? 'Approving…' : 'Approve & Book Pickup'}
            </button>
            <button
              type="button"
              className="aop-btn-danger"
              disabled={!!busy || !note.trim()}
              onClick={() => act('reject')}
            >
              {busy === 'reject' ? 'Rejecting…' : 'Reject Return'}
            </button>
          </div>
        </div>
      )}

      {['approved', 'picked_up'].includes(rr.status) && (
        <div className="aop-card-actions">
          <button
            type="button"
            className="aop-btn-primary"
            disabled={!!busy}
            onClick={() => act('received')}
          >
            <Package size={14} />
            {busy === 'received' ? 'Saving…' : 'Item Received — Restock'}
          </button>
        </div>
      )}

      {rr.status === 'received' && (
        <div className="aop-inline-form">
          <label>
            Refund Amount (₹):
            <input
              type="number"
              min="1"
              max={order.totalPrice}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          {isCod && (
            <label>
              UPI Transaction Ref (after paying {rr.upiId || 'customer'}):
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value.trim())}
                placeholder="e.g. UPI/123456789012"
              />
            </label>
          )}
          <div className="aop-form-actions">
            <button
              type="button"
              className="aop-btn-primary"
              disabled={!!busy || !amount || (isCod && !reference)}
              onClick={() => act('refund', { amount: Number(amount), reference })}
            >
              {busy === 'refund'
                ? 'Processing Refund…'
                : isCod
                ? 'Record UPI Refund'
                : `Refund ${formatPrice(Number(amount) || 0)} via Razorpay`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;

